import api from "utils/api";

const BASE = "/buccl_user/api/v1/bucketlist";

// ── BE #449 후속 요청 반영 현황 ──────────────────────────────────────────────
// 1) 달성 사진 다중 첨부 + JOURNAL 장문 필드 → 반영 (requests024)
// 2) 카테고리 enum 재정의 → BE 반영 완료 (requests025, 9종 → 새 6종, 폐기 7종은 migration에서 etc로 이관)
//    FE는 서버가 내려주는 code 기반으로 graceful 렌더(미정의 code도 표시)
// 3) 프로필 노출 → 공개(is_public)로 단일화. 별도 featured 개념은 FE에서 쓰지 않음
//    (공개=프로필 노출 대상, 최대 3개). BE의 is_featured/feature 액션은 미사용으로 남겨둠.
// 4) v2 통합 프로필(getProfileV2) 응답에 bucket_list 포함 → 반영, 별도 호출 제거됨
// ─────────────────────────────────────────────────────────────────────────────

// 버킷리스트 카테고리 enum — BE issue #449 (requests025 확정, 새 6종). code 저장 / label 표시.
// 순서·code는 BE 모델(CATEGORY_CHOICES) 기준: activity/health/travel/hobby/learning/etc.
// ※ learning 라벨은 FE 요청(학습→성장)을 선반영해 "성장"으로 노출한다. code는 아직 learning
//    (BE는 growth를 400 거부). BE가 code까지 growth로 바꾸면 이 한 줄만 갱신하면 된다.
// 서버가 미정의 code를 내려줘도 getCategoryLabel/mergeCategoryOptions가 가변 흡수한다.
export const BUCKET_CATEGORIES = [
    { code: "activity", label: "액티비티" },
    { code: "health", label: "건강" },
    { code: "travel", label: "여행" },
    { code: "hobby", label: "취미" },
    { code: "learning", label: "성장" },
    { code: "etc", label: "기타" },
];

const CATEGORY_LABEL_MAP = new Map(
    BUCKET_CATEGORIES.map((c) => [c.code, c.label])
);

// 카테고리 code → 표시 label.
// 알려진 code면 한글 label, 미정의 code(서버 enum 변경분)면 code 원문을 그대로 노출해
// 라벨이 FE에 추가되기 전에도 항목이 사라지지 않게 한다. 값이 없으면 null.
export const getCategoryLabel = (code) =>
    code ? CATEGORY_LABEL_MAP.get(code) ?? code : null;

// 정적 BUCKET_CATEGORIES + 실제 데이터에 존재하는 미정의 카테고리 code를 합쳐
// 필터 칩/드롭다운 옵션을 서버 값에 따라 가변 생성한다. (순서: 알려진 6종 우선)
export const mergeCategoryOptions = (codes = []) => {
    const known = new Set(BUCKET_CATEGORIES.map((c) => c.code));
    const extras = [];
    const seen = new Set();
    for (const code of codes) {
        if (!code || known.has(code) || seen.has(code)) continue;
        seen.add(code);
        extras.push({ code, label: getCategoryLabel(code) });
    }
    return [...BUCKET_CATEGORIES, ...extras];
};

// 본인 버킷리스트 전체 조회 (공개/비공개/달성 포함)
export const listBucketList = async () => {
    try {
        const res = await api.get(`${BASE}/`);
        return Array.isArray(res.data) ? res.data : res.data?.results ?? [];
    } catch (err) {
        console.error("버킷리스트 조회 오류:", err);
        return [];
    }
};

// 커뮤니티 프로필 노출용 (bucketlist_visible && is_public, featured 우선 상위 3개) — 비인증 허용(AllowAny).
// 통합 프로필(getProfileV2, IsAuthenticated)의 bucket_list를 우선 사용하고, 비인증 노출이 필요한
// 경로를 위한 폴백으로 남겨둔다.
export const getProfileBucketList = async (userId) => {
    if (!userId) return [];
    try {
        const res = await api.get(`${BASE}/profile/`, {
            params: { user_id: userId },
        });
        return Array.isArray(res.data) ? res.data : res.data?.results ?? [];
    } catch (err) {
        // 404(비활성 회원) 등은 노출 없음으로 처리
        return [];
    }
};

// 생성 — content(필수, 20자 이하), category/target_date/is_public/memo/journal(선택)
export const createBucketList = async (payload) => {
    const res = await api.post(`${BASE}/`, payload);
    return res.data;
};

// 부분 수정 — content/category/target_date/is_public/memo/journal (달성·featured 토글은 전용 액션)
export const updateBucketList = async (id, payload) => {
    const res = await api.patch(`${BASE}/${id}/`, payload);
    return res.data;
};

// 달성 사진 다중 업로드 — multipart `images` (1~10장, 파일당 ≤10MB) → 생성된 이미지 목록 반환
// onUploadProgress: axios 진행률 콜백 (업로드 % 표시용)
export const uploadBucketImages = async (id, files, { onUploadProgress } = {}) => {
    const formData = new FormData();
    Array.from(files).forEach((file) => formData.append("images", file));
    const res = await api.post(`${BASE}/${id}/images/`, formData, { onUploadProgress });
    return res.data;
};

// 이미지 1장 삭제 (204)
export const deleteBucketImage = async (id, imageId) => {
    const res = await api.delete(`${BASE}/${id}/images/${imageId}/`);
    return res.data;
};

// 삭제
export const deleteBucketList = async (id) => {
    const res = await api.delete(`${BASE}/${id}/`);
    return res.data;
};

// 달성/달성취소 전용 액션 — achievedAt(YYYY-MM-DD)이면 달성, null이면 달성취소
export const achieveBucketList = async (id, achievedAt) => {
    const res = await api.patch(`${BASE}/${id}/achieve/`, {
        achieved_at: achievedAt ?? null,
    });
    return res.data;
};
